import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';
import {
  LayoutDashboard,
  Home,
  Target,
  MapPin,
  Calendar,
  Image,
  Users,
  Briefcase,
  Building,
  FileText,
  MessageSquare,
  Menu,
  X,
  ChevronRight,
  BarChart3,
  HandHeart,
  Globe,
  LogOut,
} from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface SidebarItem {
  name: string;
  icon: React.ReactNode;
  path: string;
  subItems?: {
    name: string;
    path: string;
    icon: React.ReactNode;
  }[];
}

interface AdminLayoutProps {
  children: React.ReactNode;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedItems, setExpandedItems] = useState<string[]>(['Home Page']); // Default expanded
  const location = useLocation();

  const sidebarItems: SidebarItem[] = [
    {
      name: 'Dashboard',
      icon: <LayoutDashboard size={20} />,
      path: '/admin'
    },
    {
      name: 'Home Page',
      icon: <Home size={20} />,
      path: '/admin/home',
      subItems: [
        { name: 'Slides', path: '/admin/home/slides', icon: <Image size={16} /> },
        { name: 'Focus Areas', path: '/admin/home/focus-areas', icon: <Target size={16} /> },
        { name: 'Events', path: '/admin/home/events', icon: <Calendar size={16} /> },
        { name: 'Project', path: '/admin/home/project', icon: <Calendar size={16} /> },
        { name: 'Achievements', path: '/admin/home/achievements', icon: <TrendingUp size={16} /> },
        
        { name: 'About', path: '/admin/home/about', icon: <FileText size={16} /> } 
         
      ]
    },
    {
      name: 'Where We Work',
      icon: <MapPin size={20} />,
      path: '/admin/where-we-work',
      subItems: [
        { name: 'Departments', path: '/admin/departments', icon: <Building size={16} /> }
      ]
    },
    {
      name: 'Partners',
      icon: <Users size={20} />,
      path: '/admin/partners',
      subItems: [
        { name: 'All Partners', path: '/admin/partners', icon: <Users size={16} /> },
        { name: 'Add Partner', path: '/admin/partners/create', icon: <HandHeart size={16} /> }
      ]
    },
    {
      name: 'Donors',
      icon: <Globe size={20} />,
      path: '/admin/donors',
      subItems: [
        { name: 'All Donors', path: '/admin/donors', icon: <Globe size={16} /> },
        { name: 'Add Donor', path: '/admin/donors/create', icon: <HandHeart size={16} /> }
      ]
    },
    {
      name: 'Jobs',
      icon: <Briefcase size={20} />,
      path: '/admin/home/jobs'
    },
    {
      name: 'About',
      icon: <FileText size={20} />,
      path: '/admin/about'
    },
    {
      name: 'Contact',
      icon: <MessageSquare size={20} />,
      path: '/admin/contact'
    }
  ];

  const toggleItem = (name: string) => {
    setExpandedItems(prev =>
      prev.includes(name) ? prev.filter(item => item !== name) : [...prev, name]
    );
  };

  const isItemActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    try {
      await api.post('/api/logout', {}, {
        withCredentials: true,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      window.location.href = '/';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-semibold text-gray-800">Admin Panel</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <X size={20} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {sidebarItems.map((item) => (
              <div key={item.name}>
                {item.subItems ? (
                  <div className="mb-2">
                    <button
                      onClick={() => toggleItem(item.name)}
                      className={`flex items-center justify-between w-full px-4 py-3 rounded-lg transition-colors ${
                        isItemActive(item.path) ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        {item.icon}
                        <span className="font-medium">{item.name}</span>
                      </div>
                      <ChevronRight
                        size={16}
                        className={`transition-transform ${expandedItems.includes(item.name) ? 'rotate-90' : ''}`}
                      />
                    </button>
                    
                    {expandedItems.includes(item.name) && (
                      <div className="mt-1 ml-4 space-y-1">
                        {item.subItems.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            className={`flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors ${
                              location.pathname === subItem.path ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            {subItem.icon}
                            <span>{subItem.name}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      isItemActive(item.path) ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {item.icon}
                    <span className="font-medium">{item.name}</span>
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* User Profile with Logout */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold">A</span>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Admin User</p>
                  <p className="text-sm text-gray-500">Administrator</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : ''}`}>
        {/* Topbar */}
        <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-lg hover:bg-gray-100 lg:hidden"
              >
                <Menu size={20} />
              </button>
              <h1 className="text-2xl font-bold text-gray-800 capitalize">
                {location.pathname.split('/').pop()?.replace('-', ' ') || 'Dashboard'}
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button className="p-2 rounded-full hover:bg-gray-100">
                <div className="relative">
                  <MessageSquare size={20} className="text-gray-600" />
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                </div>
              </button>
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full"></div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;